import React from 'react';
import StatusBadge from './StatusBadge';
import InfoTooltip from './InfoTooltip';
import { Activity, TrendingUp, Clock, Shield, DollarSign, Zap } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * OpsHealthTile - Square card design for operational health metrics
 */
const OpsHealthTile = ({ tile }) => {
  const iconMap = {
    'API P95': Activity,
    '5xx Rate': TrendingUp,
    'DSAT Last Run': Clock,
    'EV-Yatra Fresh': Shield,
    'Credits/Gross': DollarSign,
  };

  const getInfoContent = (label) => {
    switch (label) {
      case 'API P95':
        return "95th percentile API response time. Target: <500ms.";
      case '5xx Rate':
        return "Server error rate. Target: <0.1%.";
      case 'DSAT Last Run':
        return "Time since last DSAT computation. Target: <2 hours.";
      case 'EV-Yatra Fresh':
        return "Data freshness from EV-Yatra. Target: <5 mins.";
      case 'Credits/Gross':
        return "SLA credits as % of gross revenue. Target: <3%.";
      default:
        return "Operational health metric.";
    }
  };

  const Icon = iconMap[tile.label] || Zap;

  return (
    <div className={cn(
      'relative bg-white dark:bg-gray-800 rounded-xl p-4',
      'border border-gray-200 dark:border-gray-700',
      'shadow-sm hover:shadow-md transition-all duration-200',
      'flex flex-col justify-between aspect-square',
      'hover:scale-[1.02] cursor-pointer'
    )}>
      {/* Header Row */}
      <div className="flex items-start justify-between">
        <div className={cn(
          'p-2 rounded-lg',
          'bg-blue-50 dark:bg-blue-900/30'
        )}>
          <Icon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
        </div>
        <StatusBadge status={tile.badge} size="xs" />
      </div>
      
      {/* Value - Centered */}
      <div className="flex-1 flex flex-col justify-center">
        <p className="text-2xl font-bold text-gray-900 dark:text-white">
          {tile.value}
        </p>
      </div>
      
      {/* Footer */}
      <div className="space-y-1">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-400 truncate">
            {tile.label}
          </span>
          <InfoTooltip content={getInfoContent(tile.label)} />
        </div>
        <div className="flex items-center justify-between text-[10px] text-gray-500 dark:text-gray-500">
          <span>{tile.source}</span>
          <span>• {new Date(tile.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
        </div>
      </div>
    </div>
  );
};

/**
 * OpsHealthStrip - Compact operational health dashboard with square cards
 */
const OpsHealthStrip = ({ opsHealth }) => {
  if (!opsHealth || !opsHealth.tiles || opsHealth.tiles.length === 0) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 mb-4">
        <div className="animate-pulse">
          <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-40 mb-4"></div>
          <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="aspect-square bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-gray-50 to-blue-50/50 dark:from-gray-800/50 dark:to-blue-900/20 rounded-xl p-4 mb-4 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
            Operational Health
          </h2>
        </div>
        <span className="text-[10px] text-gray-500 dark:text-gray-400">
          Live monitoring • Updates every 30s
        </span>
      </div>
      
      <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-3">
        {opsHealth.tiles.map((tile, idx) => (
          <OpsHealthTile key={idx} tile={tile} />
        ))}
      </div>
    </div>
  );
};

export default OpsHealthStrip;
